import React, { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Button } from "@/components/ui/button";
import { X, Camera, Keyboard } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";

/**
 * Barcode / QR scanner using html5-qrcode + the device's rear camera.
 * Falls back to a manual entry mode when the camera isn't available.
 *
 * Props:
 *   - onScan(text): fired when a barcode is detected (or typed manually)
 *   - onClose(): fired when the user closes the scanner
 */
export default function BarcodeScanner({ onScan, onClose }) {
  const containerId = useRef(`bc-scanner-${Math.random().toString(36).slice(2)}`);
  const scannerRef = useRef(null);
  const [running, setRunning] = useState(false);
  const [err, setErr] = useState(null);
  const [manual, setManual] = useState("");
  const [manualMode, setManualMode] = useState(false);

  useEffect(() => {
    if (manualMode) return;
    const start = async () => {
      try {
        const scanner = new Html5Qrcode(containerId.current, { verbose: false });
        scannerRef.current = scanner;
        await scanner.start(
          { facingMode: { exact: "environment" } },
          { fps: 12, qrbox: { width: 260, height: 130 } },
          (decoded) => {
            // Stop immediately to prevent rapid re-fires
            scanner.stop().catch(() => {}).finally(() => onScan(decoded));
          },
          () => { /* swallow decode-failure noise */ }
        );
        setRunning(true);
      } catch (e) {
        // Retry with relaxed facingMode
        try {
          const scanner = new Html5Qrcode(containerId.current, { verbose: false });
          scannerRef.current = scanner;
          await scanner.start(
            { facingMode: "environment" },
            { fps: 12, qrbox: { width: 260, height: 130 } },
            (decoded) => {
              scanner.stop().catch(() => {}).finally(() => onScan(decoded));
            },
            () => {}
          );
          setRunning(true);
        } catch (e2) {
          setErr("Couldn't start the camera. Make sure you've granted camera permissions.");
        }
      }
    };
    start();
    return () => {
      const s = scannerRef.current;
      if (s) {
        try { s.stop().catch(() => {}); } catch { /* ignore */ }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manualMode]);

  const submitManual = () => {
    const v = manual.trim().toUpperCase();
    if (v.length < 4) return;
    onScan(v);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-3" data-testid="barcode-scanner">
      <div className="w-full max-w-md bg-zinc-900 rounded-md overflow-hidden">
        <div className="px-4 py-3 flex items-center justify-between border-b border-zinc-700">
          <div className="text-white text-sm font-semibold flex items-center gap-2">
            <Camera size={16} weight="bold" /> Scan package barcode
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white" data-testid="barcode-close">
            <X size={18} weight="bold" />
          </button>
        </div>
        {!manualMode ? (
          <>
            <div id={containerId.current} className="w-full" style={{ minHeight: 280 }} data-testid="barcode-video" />
            {err && (
              <div className="px-4 py-3 text-xs text-red-300 border-t border-zinc-700" data-testid="barcode-error">
                {err}
              </div>
            )}
            <div className="px-4 py-3 border-t border-zinc-700 flex justify-between gap-2 items-center text-zinc-200 text-xs">
              <span>{running ? "Point the camera at the barcode" : "Starting camera…"}</span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setManualMode(true)}
                className="bg-transparent border-zinc-600 text-zinc-200 hover:bg-zinc-800"
                data-testid="barcode-manual-mode"
              >
                <Keyboard size={13} className="mr-1.5" /> Enter manually
              </Button>
            </div>
          </>
        ) : (
          <div className="p-5 space-y-3">
            <div className="text-xs text-zinc-300">
              Type the tracking number printed on the package shipping label (e.g. <span className="font-mono">KN8503732787</span>).
            </div>
            <Input
              value={manual}
              onChange={(e) => setManual(e.target.value)}
              placeholder="Tracking number"
              className="font-mono uppercase bg-zinc-800 text-white border-zinc-700"
              autoFocus
              data-testid="barcode-manual-input"
              onKeyDown={(e) => e.key === "Enter" && submitManual()}
            />
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setManualMode(false)} className="bg-transparent border-zinc-600 text-zinc-200 hover:bg-zinc-800" data-testid="barcode-back-to-camera">
                <Camera size={13} className="mr-1.5" /> Back to camera
              </Button>
              <Button size="sm" onClick={submitManual} disabled={!manual.trim()} className="bg-[#002BF6] hover:bg-[#0021C7] text-white" data-testid="barcode-manual-submit">
                Load package
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
